import React from 'react';
import styles from './Certificates.module.scss';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import Card from '@/components/ui/Card/Card.tsx';
import ButtonLink from '@/components/ui/ButtonLink/ButtonLink.tsx';
import CertificateIllustration from '@/assets/Illustrations/Certificate-With-Background.svg?react';
import DownloadIcon from '@/assets/Icons/Download.svg?react';
import ShareIcon from '@/assets/Icons/Share.svg?react';
import { CellContext } from '@tanstack/react-table';
import { useModalStore } from '@/store/useModalStore.ts';
import ShareModal from '@/pages/ClimateProDashboard/Certificates/ShareModal.tsx';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys.ts';
import { getCertificates } from '@/api/climatePro.ts';
import { formatWeight } from '@/utils/stringUtils.ts';
import moment from 'moment';
import Tooltip from '@/components/ui/Tooltip/Tooltip.tsx';
import { externalLinks } from '@/constants/externalLinks.ts';
import ClimateProCertificateData = App.Data.ClimateProCertificateData;
import breakpoints from '@/constants/breakpoints.ts';
import { trackEvent } from '@/utils/productAnalyticsUtil.ts';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';

const certificatesColumns = [
	{
		accessorKey: 'year',
		header: 'Year',
		type: 'number',
	},
	{
		accessorKey: 'issueDate',
		header: 'Date issued',
		type: 'text',
		cell: ({ getValue }: CellContext<any, string>) => {
			const value = getValue();
			return moment(value).format('DD MMM YYYY');
		},
	},
	{
		accessorKey: 'co2eConsumed',
		header: 'CO₂e consumed',
		type: 'number',
		cell: ({ getValue }: CellContext<any, number | string>) => {
			const value = getValue();
			return formatWeight(+value);
		},
	},
	{
		accessorKey: 'co2eRemoved',
		header: 'CO₂e removed',
		type: 'number',
		cell: ({ getValue }: CellContext<any, number>) => {
			const value = getValue();
			return formatWeight(value);
		},
	},
	{
		accessorKey: 'co2eReduced',
		header: 'CO₂e reduced',
		type: 'number',
		cell: ({ getValue }: CellContext<any, string | number | null>) => {
			const value = getValue();
			return value ? formatWeight(+value) : '-';
		},
	},
	{
		accessorKey: 'reduceUrl',
		header: 'Reduction link',
		type: 'text',
		cell: ({ getValue }: CellContext<any, string>) => {
			const url = getValue();
			return url ? (
				<a
					href={url}
					target="_blank"
					className={styles.tableLinkCertificates}
					onClick={() =>
						trackEvent(productAnalyticsEvents.climatePro.compensationStatement.overview.interact, {
							deduplicationKey: `reduction-link.${url}`,
						})
					}
				>
					link
				</a>
			) : (
				'-'
			);
		},
	},
	{
		accessorKey: 'removeUrl',
		header: 'Removal link',
		type: 'text',
		cell: ({ getValue }: CellContext<any, string>) => {
			const url = getValue<string>();
			return url ? (
				<a
					href={url}
					target="_blank"
					className={styles.tableLinkCertificates}
					onClick={() =>
						trackEvent(productAnalyticsEvents.climatePro.compensationStatement.overview.interact, {
							deduplicationKey: `removal-link.${url}`,
						})
					}
				>
					link
				</a>
			) : (
				'-'
			);
		},
	},
	{
		accessorKey: 'ownerEntity',
		header: 'Owner',
		type: 'text',
	},
];

const Certificates: React.FC = () => {
	const { openModal } = useModalStore(store => store);

	const { data, isSuccess } = useQuery({
		queryKey: [queryKeys.certificates],
		queryFn: getCertificates,
	});

	const handleDownload = (rowId: number) => {
		const url = data?.certificates.find(certificate => certificate.id === rowId)?.downloadUrl;
		window.open(url, '_blank');
	};

	const handleShare = (certificateId: number) => {
		openModal('certificateShareModal', {
			downloadUrl: data?.certificates.find(certificate => certificate.id.toString() === certificateId.toString())
				?.downloadUrl,
		});
	};

	const additionalActions: any[] = [
		{
			icon: <DownloadIcon />,
			onClick: handleDownload,
			tooltipText: 'Download',
		},
		{
			icon: <ShareIcon />,
			onClick: handleShare,
			tooltipText: 'Share',
		},
	];

	return (
		<FlexBlock flexDirection="column" rowGap="32px" className={styles.certificatesWrapper}>
			<h1>Climate impact certificates</h1>
			<p className={styles.additionalInfo}>
				Get an overview of your company’s travel emissions with a detailed report.
			</p>
			<Card className={styles.certificatesCard}>
				<FlexBlock
					flexDirection={{
						[breakpoints.zero]: 'column',
						[breakpoints.tablet]: 'row',
					}}
					columnGap="20px"
					justifyContent="space-between"
				>
					<div>
						<h2>What is a climate impact certificate?</h2>
						<p>
							A climate impact certificate (CO₂ certificate) quantifies your company’s carbon emissions,
							ensuring transparency and demonstrating your commitment to sustainability.
						</p>
						<ButtonLink
							color="default"
							size="medium"
							href={externalLinks.certificateExample}
							target="_blank"
							onClick={() =>
								trackEvent(productAnalyticsEvents.climatePro.compensationStatement.sample.download, {
									postHogEventName: 'download_sample_certificate',
									postHogProperties: { category: 'downloads' },
								})
							}
						>
							Download sample certificate
						</ButtonLink>
					</div>
					<div className={styles.illustrationWrapper}>
						<CertificateIllustration />
					</div>
				</FlexBlock>
			</Card>
			{isSuccess && data?.certificates.length > 0 && (
				<FlexBlock flexDirection="column" rowGap="32px" className={styles.tableSection}>
					<h2>Climate Certificate Archive</h2>
					<div className={styles.tableWrapper}>
						<table>
							<thead>
								<tr>
									{certificatesColumns.map((column, index) => (
										<th key={index}>{column.header}</th>
									))}
									<th>Actions</th>
								</tr>
							</thead>
							<tbody>
								{data.certificates.map(certificate => (
									<tr key={certificate.id}>
										{certificatesColumns.map((column, index) => (
											<td key={index}>
												{column.cell
													? column.cell({
															getValue: () =>
																certificate[
																	column.accessorKey as keyof ClimateProCertificateData
																] || '',
														} as any)
													: certificate[
															column.accessorKey as keyof ClimateProCertificateData
														]}
											</td>
										))}
										<td>
											<FlexBlock columnGap="10px">
												{additionalActions.map((action, index) => (
													<Tooltip
														icon={
															<button
																onClick={() => {
																	const isDownload =
																		action.tooltipText === 'Download';
																	trackEvent(
																		isDownload
																			? productAnalyticsEvents.climatePro
																					.compensationStatement.file.download
																			: productAnalyticsEvents.climatePro
																					.compensationStatement.overview
																					.interact,
																		{
																			deduplicationKey: `${action.tooltipText.toLowerCase()}.${certificate.id}`,
																			postHogEventName: `certificate_action_${action.tooltipText.toLowerCase()}_clicked`,
																			postHogProperties: {
																				category: isDownload
																					? 'downloads'
																					: 'certificates',
																			},
																		},
																	);
																	action.onClick(certificate.id);
																}}
																className={styles.actionBttn}
															>
																{action.icon}
															</button>
														}
														text={action.tooltipText}
														key={index}
													/>
												))}
											</FlexBlock>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</FlexBlock>
			)}
			<ShareModal />
		</FlexBlock>
	);
};

export default Certificates;
